<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import {
  additionalInputFromSavedTotal,
  combinedGivingPence,
} from '@/helpers/claimTaxBack/checkerGiving'
import {
  CLAIM_FUNNEL_EVENTS,
  trackClaimFunnel,
} from '@/helpers/claimTaxBack/funnel'
import {
  MAX_MANUAL_GIVING_PENCE,
  V1_CLAIMABLE_PERIOD_KEY,
} from '@/helpers/claimTaxBack/taxYearConfig'
import type { ClaimTaxRegion } from '@/types/claimTaxBack'
import type { UserStatement } from '@/types/userStatement'
import debug from '@/helpers/debug'
import SaSpinner from '@/components/dashboard/ui/SaSpinner.vue'
import PaidPlanBadge from '@/components/dashboard/ui/PaidPlanBadge.vue'
import ClaimableTotalHint from '@/components/dashboard/claim/ClaimableTotalHint.vue'
import ClaimFaq from '@/components/dashboard/claim/ClaimFaq.vue'
import claimCheckIllustration from '@/assets/images/claim-tax-back-check.svg'

/** HMRC's own page for higher-rate taxpayers claiming Gift Aid relief. */
const HMRC_HIGHER_RATE_GUIDANCE_URL =
  'https://www.gov.uk/donating-to-charity/gift-aid#if-youre-a-higher-rate-taxpayer'

const emit = defineEmits<{
  /** Open the standard upgrade modal (claim-led context comes from the shared profile). */
  upgrade: []
}>()

const { t } = useI18n()
const store = useStore()
const {
  profile,
  hasCompletedChecker,
  estimate,
  saveCheckerResult,
  resetChecker,
} = useClaimTaxBack()

// Free donors have a statement, not a donations list. The claim-year total
// is included as a fixed line; anything else they add is on top.
const statements = computed(
  () => store.getters.userStatements as UserStatement[] | null
)
const statementLookupDone = ref(statements.value !== null)

if (statements.value === null) {
  debug('[claim] fetching statements for the checker giving total')
  void store.dispatch('fetchUserStatements').finally(() => {
    statementLookupDone.value = true
  })
}

/** Null while the statement list is still loading. Zero when this year has none. */
const statementGivingPence = computed<number | null>(() => {
  if (!statementLookupDone.value) return null
  const statement = statements.value?.find(
    (row) => row.period === V1_CLAIMABLE_PERIOD_KEY
  )
  if (!statement || statement.totalDonated <= 0) return 0
  return statement.totalDonated
})

const hasStatementGiving = computed(
  () => (statementGivingPence.value ?? 0) > 0
)

const yearLabel = claimTaxYearConfig.yearLabel

// One card: the header stays put and the questions expand beneath it once
// they press Start. Three questions, under a minute.
const view = ref<'checker' | 'result'>(
  hasCompletedChecker.value ? 'result' : 'checker'
)
const hasStarted = ref(false)

const region = ref<ClaimTaxRegion | null>(profile.value?.region ?? null)
const incomeBandId = ref<string | null>(profile.value?.incomeBandId ?? null)
// Extra donations when a statement is included, otherwise the whole amount.
const givingInput = ref('')
const givingEdited = ref(false)

const isSaving = ref(false)
const formError = ref<string | null>(null)
const resultHeading = ref<HTMLElement | null>(null)
const regionField = ref<HTMLElement | null>(null)

// A saved profile can arrive after mount (module-level load); sync once.
watch(hasCompletedChecker, (completed) => {
  if (completed && view.value !== 'result' && !isSaving.value) {
    region.value = profile.value?.region ?? null
    incomeBandId.value = profile.value?.incomeBandId ?? null
    view.value = 'result'
  }
})

const bandsForRegion = computed(() =>
  region.value ? claimTaxYearConfig.bands[region.value] : []
)

const selectedBandRate = computed(() => {
  const band = bandsForRegion.value.find((item) => item.id === incomeBandId.value)
  if (!band || band.marginalRatePercent <= claimTaxYearConfig.basicRatePercent) {
    return null
  }
  return band.marginalRatePercent
})

// Bands differ per region, so a stale selection must never survive a switch.
watch(region, () => {
  incomeBandId.value = null
})

// Open a saved total as "statement + the rest", once we know the statement.
watch(
  [() => profile.value?.manualGivingPence, statementGivingPence],
  ([total, statement]) => {
    if (givingEdited.value || total == null || statement == null) return
    givingInput.value = additionalInputFromSavedTotal(total, statement)
  },
  { immediate: true }
)

watch(statementGivingPence, (pence) => {
  if (pence == null) return
  debug('[claim] checker statement giving', { statementPence: pence })
})

const givingTotalPence = computed(() => {
  const statement = statementGivingPence.value
  if (statement == null || statement <= 0) return null
  return combinedGivingPence(statement, givingInput.value)
})

const onGivingInput = () => {
  givingEdited.value = true
}

// The submit button only appears once the last question is on screen.
const canSubmit = computed(() => Boolean(region.value && incomeBandId.value))

const isOwedSomething = computed(
  () => estimate.value !== null && estimate.value.claimablePence > 0
)

type WorkingRow = {
  id: string
  label: string
  value: string
  tone?: 'claim'
}

const estimateRows = computed((): WorkingRow[] => {
  if (!estimate.value) return []

  // Illustration from the amount the donor entered and the rate they selected.
  // Gift Aid is 25% of that amount; the extra relief is the gap above 20%
  // applied to the gross. It is not a statement of tax they actually paid.
  return [
    {
      id: 'given',
      label: t('Dashboard.ClaimTaxBack.Result.RowGiven'),
      value: formatMoneyFromPence(estimate.value.givenPence),
    },
    {
      id: 'gift-aid',
      label: t('Dashboard.ClaimTaxBack.Result.RowGiftAid'),
      value: t('Dashboard.ClaimTaxBack.Result.RowPlus', {
        amount: formatMoneyFromPence(estimate.value.giftAidPence),
      }),
    },
    {
      id: 'gross',
      label: t('Dashboard.ClaimTaxBack.Result.RowGross'),
      value: formatMoneyFromPence(estimate.value.grossPence),
    },
    {
      id: 'claim',
      label: t('Dashboard.ClaimTaxBack.Result.RowClaimable'),
      value: t('Dashboard.ClaimTaxBack.AmountUpTo', {
        amount: formatMoneyFromPence(estimate.value.claimablePence),
      }),
      tone: 'claim',
    },
  ]
})

const focusResultHeading = async () => {
  await nextTick()
  resultHeading.value?.focus()
}

/** Start button: reveal the first question in the same card and focus it. */
const startChecker = async () => {
  trackClaimFunnel(CLAIM_FUNNEL_EVENTS.CheckerStart, {
    period_key: claimTaxYearConfig.periodKey,
  })
  hasStarted.value = true
  await nextTick()
  // The clicked button is removed with the intro; the browser resets focus
  // to body after that, so wait a frame before moving it to the question.
  requestAnimationFrame(() => regionField.value?.focus())
}

const submitChecker = async () => {
  formError.value = null

  if (!region.value) {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorRegion')
    return
  }
  if (!incomeBandId.value) {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorBand')
    return
  }
  const statementPence = statementGivingPence.value ?? 0
  const givingPence = combinedGivingPence(statementPence, givingInput.value)
  if (givingPence === null) {
    formError.value = t(
      hasStatementGiving.value
        ? 'Dashboard.ClaimTaxBack.Checker.ErrorAmountOther'
        : 'Dashboard.ClaimTaxBack.Checker.ErrorAmount'
    )
    return
  }
  // The API rejects anything above the cap with a 400; stop here instead.
  if (givingPence > MAX_MANUAL_GIVING_PENCE) {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorAmountTooLarge')
    return
  }
  debug('[claim] checker giving total', {
    statementPence,
    totalPence: givingPence,
  })

  isSaving.value = true
  try {
    await saveCheckerResult({
      region: region.value,
      incomeBandId: incomeBandId.value,
      manualGivingPence: givingPence,
    })
    trackClaimFunnel(CLAIM_FUNNEL_EVENTS.CheckerComplete, {
      period_key: claimTaxYearConfig.periodKey,
      high_rate: Boolean(profile.value?.highRateTaxpayer),
      claimable_pence: estimate.value?.claimablePence,
    })
    view.value = 'result'
    await focusResultHeading()
  } catch {
    formError.value = t('Dashboard.ClaimTaxBack.Checker.ErrorSave')
  } finally {
    isSaving.value = false
  }
}

const changeAnswers = async () => {
  // Keep the current values on screen for editing; only the saved state
  // clears. They have seen the intro, so the questions open straight away.
  hasStarted.value = true
  view.value = 'checker'
  try {
    await resetChecker()
  } catch {
    // Saving again from the form recovers this; nothing actionable here.
  }
}

const onUpgrade = () => {
  trackClaimFunnel(CLAIM_FUNNEL_EVENTS.UpgradeFromEstimate, {
    period_key: claimTaxYearConfig.periodKey,
    high_rate: Boolean(profile.value?.highRateTaxpayer),
    claimable_pence: estimate.value?.claimablePence,
  })
  emit('upgrade')
}

/** The HMRC route leaves the app. Record the choice, then let the link open. */
const onHmrcGuidance = () => {
  trackClaimFunnel(CLAIM_FUNNEL_EVENTS.HmrcGuidanceFromEstimate, {
    period_key: claimTaxYearConfig.periodKey,
    high_rate: Boolean(profile.value?.highRateTaxpayer),
    claimable_pence: estimate.value?.claimablePence,
  })
}

// Native details hides its body immediately, so the height is animated here
// and open is removed only after the panel has eased shut.
const WORKING_REVEAL_MS = 450
const workingDetails = ref<HTMLDetailsElement | null>(null)
const workingExpanded = ref(false)
const workingClosing = ref(false)
let workingMotion = 0

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const workingRevealEl = (details: HTMLDetailsElement) =>
  details.querySelector<HTMLElement>('.sa-claim-result__working-reveal')

const REVEAL_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'

const animateReveal = (
  reveal: HTMLElement,
  from: number,
  to: number,
  fromOpacity: number,
  toOpacity: number,
) => {
  reveal.getAnimations().forEach((animation) => animation.cancel())
  reveal.style.height = `${from}px`
  reveal.style.opacity = String(fromOpacity)
  return reveal.animate(
    [
      { height: `${from}px`, opacity: fromOpacity },
      { height: `${to}px`, opacity: toOpacity },
    ],
    { duration: WORKING_REVEAL_MS, easing: REVEAL_EASE, fill: 'forwards' },
  )
}

const onWorkingSummaryClick = (event: MouseEvent) => {
  const details = workingDetails.value
  const reveal = details ? workingRevealEl(details) : null
  if (!details || !reveal || prefersReducedMotion()) return

  event.preventDefault()
  const motion = ++workingMotion

  if (workingExpanded.value) {
    const openHeight = reveal.getBoundingClientRect().height
    workingExpanded.value = false
    workingClosing.value = true
    debug('[claim] working disclosure closing')
    const animation = animateReveal(reveal, openHeight, 0, 1, 0)
    animation.onfinish = () => {
      if (motion !== workingMotion || !details.isConnected) return
      details.open = false
      workingClosing.value = false
      reveal.style.height = ''
      reveal.style.opacity = ''
      animation.cancel()
    }
    return
  }

  details.open = true
  workingClosing.value = false
  reveal.style.height = '0px'
  reveal.style.opacity = '0'
  debug('[claim] working disclosure opening')
  // The body is not laid out in the turn details opens, so measure and
  // start the animation on the next paint.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (motion !== workingMotion || !details.isConnected) return
      reveal.style.height = 'auto'
      const fullHeight = reveal.scrollHeight
      workingExpanded.value = true
      const animation = animateReveal(reveal, 0, fullHeight, 0, 1)
      animation.onfinish = () => {
        if (motion !== workingMotion || !details.isConnected) return
        reveal.style.height = 'auto'
        reveal.style.opacity = ''
        animation.cancel()
      }
    })
  })
}

</script>

<template>
  <div class="sa-claim-checker">
    <!-- One card throughout: header on top, Start beneath it, then the
         questions expand in place so nothing feels like a page change. -->
    <form
      v-if="view === 'checker'"
      class="sa-card sa-card--padded sa-claim-card"
      novalidate
      aria-labelledby="claim-checker-heading"
      :aria-describedby="formError ? 'claim-checker-error' : undefined"
      @submit.prevent="submitChecker"
    >
      <div class="sa-claim-intro">
        <img
          :src="claimCheckIllustration"
          class="sa-claim-intro__illustration"
          alt=""
          width="160"
          height="112"
        />
        <h2 id="claim-checker-heading" class="sa-claim-card__title">
          {{ t('Dashboard.ClaimTaxBack.Checker.Title', { year: yearLabel }) }}
        </h2>
        <p class="sa-claim-card__intro">
          {{ t('Dashboard.ClaimTaxBack.Checker.Intro', { year: yearLabel }) }}
        </p>
      </div>

      <template v-if="!hasStarted">
        <button
          type="button"
          class="sa-btn-purple sa-btn-purple--block"
          @click="startChecker"
        >
          {{ t('Dashboard.ClaimTaxBack.Checker.Start') }}
        </button>
        <p class="sa-claim-card__footnote">
          {{ t('Dashboard.ClaimTaxBack.Checker.Duration') }}
        </p>
      </template>

      <!-- Focus lands on the group from the Start button so the first
           question is announced. A legend cannot take focus in Chrome. -->
      <fieldset
        v-if="hasStarted"
        ref="regionField"
        class="sa-claim-field"
        tabindex="-1"
      >
        <legend class="sa-claim-field__legend">
          {{ t('Dashboard.ClaimTaxBack.Checker.RegionLegend') }}
        </legend>
        <p class="sa-claim-field__help" id="claim-region-help">
          {{ t('Dashboard.ClaimTaxBack.Checker.RegionHelp') }}
        </p>
        <div class="sa-claim-options" aria-describedby="claim-region-help">
          <label
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': region === 'ruk' }"
          >
            <input
              v-model="region"
              class="sa-sr-only"
              type="radio"
              name="claim-region"
              value="ruk"
            />
            <span>{{ t('Dashboard.ClaimTaxBack.Checker.RegionRuk') }}</span>
          </label>
          <label
            class="sa-claim-option"
            :class="{ 'sa-claim-option--selected': region === 'scotland' }"
          >
            <input
              v-model="region"
              class="sa-sr-only"
              type="radio"
              name="claim-region"
              value="scotland"
            />
            <span>
              {{ t('Dashboard.ClaimTaxBack.Checker.RegionScotland') }}
            </span>
          </label>
        </div>
      </fieldset>

      <fieldset v-if="hasStarted && region" class="sa-claim-field">
        <legend class="sa-claim-field__legend">
          {{
            t('Dashboard.ClaimTaxBack.Checker.IncomeLegend', { year: yearLabel })
          }}
        </legend>
        <p class="sa-claim-field__help" id="claim-income-help">
          {{ t('Dashboard.ClaimTaxBack.Checker.IncomeHelp') }}
        </p>
        <div class="sa-claim-options" aria-describedby="claim-income-help">
          <template v-for="band in bandsForRegion" :key="band.id">
            <label
              class="sa-claim-option"
              :class="{ 'sa-claim-option--selected': incomeBandId === band.id }"
            >
              <input
                v-model="incomeBandId"
                class="sa-sr-only"
                type="radio"
                name="claim-income-band"
                :value="band.id"
              />
              <span>{{ band.label }}</span>
            </label>
            <p
              v-if="selectedBandRate && incomeBandId === band.id"
              class="sa-claim-field__help sa-claim-option-note"
            >
              {{
                t('Dashboard.ClaimTaxBack.Checker.BandRateNote', {
                  rate: selectedBandRate,
                })
              }}
            </p>
          </template>
        </div>
      </fieldset>

      <div v-if="hasStarted && canSubmit" class="sa-claim-field">
        <p
          v-if="statementGivingPence === null"
          class="sa-claim-field__help"
          role="status"
        >
          {{ t('Dashboard.ClaimTaxBack.Checker.GivingChecking') }}
        </p>

        <!-- Statement total is fixed. Anything else they type is added on. -->
        <div
          v-else-if="hasStatementGiving"
          class="sa-claim-giving"
          role="group"
          aria-labelledby="claim-giving-legend"
        >
          <p class="sa-claim-field__legend" id="claim-giving-legend">
            {{
              t('Dashboard.ClaimTaxBack.Checker.GivingLabel', {
                year: yearLabel,
              })
            }}
          </p>
          <div class="sa-claim-giving__statement">
            <span class="sa-claim-giving__label">
              {{ t('Dashboard.ClaimTaxBack.Checker.GivingStatementLabel') }}
            </span>
            <span class="sa-claim-giving__value">
              {{ formatMoneyFromPence(statementGivingPence ?? 0) }}
            </span>
          </div>
          <div class="sa-claim-giving__other">
            <label class="sa-claim-field__legend" for="claim-giving-amount">
              {{ t('Dashboard.ClaimTaxBack.Checker.GivingOtherLabel') }}
            </label>
            <p class="sa-claim-field__help" id="claim-giving-help">
              {{ t('Dashboard.ClaimTaxBack.Checker.GivingOtherHelp') }}
            </p>
            <div class="sa-claim-amount">
              <span class="sa-claim-amount__symbol" aria-hidden="true">£</span>
              <input
                id="claim-giving-amount"
                v-model="givingInput"
                class="sa-input sa-claim-amount__input"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                :placeholder="
                  t('Dashboard.ClaimTaxBack.Checker.GivingPlaceholder')
                "
                :aria-invalid="formError ? true : undefined"
                :aria-describedby="
                  formError
                    ? 'claim-giving-help claim-checker-error'
                    : 'claim-giving-help'
                "
                @input="onGivingInput"
              />
            </div>
          </div>
          <p v-if="givingTotalPence != null" class="sa-claim-giving__total">
            <span>{{ t('Dashboard.ClaimTaxBack.Checker.GivingTotalLabel') }}</span>
            <span aria-live="polite">
              {{ formatMoneyFromPence(givingTotalPence) }}
            </span>
          </p>
        </div>

        <template v-else>
          <label class="sa-claim-field__legend" for="claim-giving-amount">
            {{
              t('Dashboard.ClaimTaxBack.Checker.GivingLabel', {
                year: yearLabel,
              })
            }}
          </label>
          <p class="sa-claim-field__help" id="claim-giving-help">
            {{ t('Dashboard.ClaimTaxBack.Checker.GivingHelp') }}
          </p>
          <div class="sa-claim-amount">
            <span class="sa-claim-amount__symbol" aria-hidden="true">£</span>
            <input
              id="claim-giving-amount"
              v-model="givingInput"
              class="sa-input sa-claim-amount__input"
              type="text"
              inputmode="decimal"
              autocomplete="off"
              :placeholder="
                t('Dashboard.ClaimTaxBack.Checker.GivingPlaceholder')
              "
              :aria-invalid="formError ? true : undefined"
              :aria-describedby="
                formError
                  ? 'claim-giving-help claim-checker-error'
                  : 'claim-giving-help'
              "
              aria-required="true"
              @input="onGivingInput"
            />
          </div>
        </template>
      </div>

      <p v-if="formError" id="claim-checker-error" class="sa-field-error" role="alert">
        {{ formError }}
      </p>

      <button
        v-if="hasStarted && canSubmit"
        type="submit"
        class="sa-btn-purple sa-btn-purple--block"
        :disabled="isSaving"
        :aria-busy="isSaving"
      >
        <SaSpinner v-if="isSaving" size="button" />
        {{ t('Dashboard.ClaimTaxBack.Checker.Submit') }}
      </button>
    </form>

    <div v-else-if="estimate" class="sa-claim-result">
      <!-- Owed: the estimate card is the donor's number and working only; the
           sell lives in its own card below so the figure stays untainted. -->
      <template v-if="isOwedSomething">
        <section
          class="sa-card sa-card--padded sa-claim-card"
          aria-labelledby="claim-result-heading"
        >
          <div class="sa-claim-result__hero">
            <p class="sa-claim-result__eyebrow">
              {{
                t('Dashboard.ClaimTaxBack.Result.Eyebrow', { year: yearLabel })
              }}
            </p>
            <h2
              id="claim-result-heading"
              ref="resultHeading"
              class="sa-claim-result__headline"
              tabindex="-1"
            >
              <span class="sa-claim-result__label">
                {{ t('Dashboard.ClaimTaxBack.Result.Headline') }}
              </span>
              <span class="sa-sr-only">
                {{
                  t('Dashboard.ClaimTaxBack.AmountUpTo', {
                    amount: formatMoneyFromPence(estimate.claimablePence),
                  })
                }}
              </span>
            </h2>
            <!-- "Up to" stays with the figure. The estimate note sits under it. -->
            <ClaimableTotalHint conceal-value>
              <span class="sa-claim-result__amount">
                {{ formatMoneyFromPence(estimate.claimablePence) }}
              </span>
            </ClaimableTotalHint>
          </div>

          <!-- Closed by default, as on the public checker, so the estimate
               and the next choice stay in front. -->
          <details
            ref="workingDetails"
            class="sa-claim-details sa-claim-result__working"
            :class="{
              'is-open': workingExpanded,
              'is-closing': workingClosing,
            }"
          >
            <summary
              class="sa-claim-details__summary"
              @click="onWorkingSummaryClick"
            >
              {{ t('Dashboard.ClaimTaxBack.Result.WorkingHeading') }}
            </summary>
            <div class="sa-claim-result__working-reveal">
              <div class="sa-claim-result__working-panel">
                  <dl class="sa-claim-working">
                    <div
                      v-for="row in estimateRows"
                      :key="row.id"
                      class="sa-claim-working__row"
                      :class="row.tone && `sa-claim-working__row--${row.tone}`"
                    >
                      <dt>{{ row.label }}</dt>
                      <dd>{{ row.value }}</dd>
                    </div>
                  </dl>
                  <p class="sa-claim-result__explain">
                    {{
                      t('Dashboard.ClaimTaxBack.Result.Explain', {
                        rate: estimate.marginalRatePercent,
                        basicRate: claimTaxYearConfig.basicRatePercent,
                        gross: formatMoneyFromPence(estimate.grossPence),
                      })
                    }}
                  </p>
                  <p class="sa-claim-result__explain">
                    {{ t('Dashboard.ClaimTaxBack.Result.ExplainLimit') }}
                  </p>
              </div>
            </div>
          </details>

          <div class="sa-claim-result__follow">
            <button
              type="button"
              class="sa-claim-link-btn"
              @click="changeAnswers"
            >
              {{ t('Dashboard.ClaimTaxBack.Result.ChangeAnswers') }}
            </button>
          </div>
        </section>

        <!-- Two equal routes. The subscription price sits under the upgrade button. -->
        <section
          class="sa-card sa-card--padded sa-claim-card sa-claim-routes"
          aria-labelledby="claim-routes-heading"
        >
          <h2 id="claim-routes-heading" class="sa-claim-routes__title">
            {{ t('Dashboard.ClaimTaxBack.Result.NextTitle') }}
          </h2>
          <div class="sa-claim-routes__grid">
            <article class="sa-claim-route">
              <div class="sa-claim-route__copy">
                <h3 class="sa-claim-route__title">
                  {{ t('Dashboard.ClaimTaxBack.Result.HmrcTitle') }}
                </h3>
                <p class="sa-claim-route__body">
                  {{ t('Dashboard.ClaimTaxBack.Result.HmrcBody') }}
                </p>
              </div>
              <a
                class="sa-btn-secondary sa-claim-route__action"
                :href="HMRC_HIGHER_RATE_GUIDANCE_URL"
                target="_blank"
                rel="noopener noreferrer"
                @click="onHmrcGuidance"
              >
                {{ t('Dashboard.ClaimTaxBack.Result.HmrcCta') }}
                <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                  <path
                    fill="currentColor"
                    d="M11 3a1 1 0 100 2h2.586L9.293 9.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                  />
                  <path
                    fill="currentColor"
                    d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"
                  />
                </svg>
                <span class="sa-sr-only">
                  ({{ t('Dashboard.ClaimTaxBack.Result.HmrcNewTab') }})
                </span>
              </a>
            </article>
            <article class="sa-claim-route sa-claim-route--swiftaid">
              <div class="sa-claim-route__copy">
                <h3 class="sa-claim-route__title">
                  {{ t('Dashboard.ClaimTaxBack.Result.SwiftaidTitle') }}
                </h3>
                <p class="sa-claim-route__body">
                  {{ t('Dashboard.ClaimTaxBack.Result.SwiftaidBody') }}
                </p>
                <p class="sa-claim-route__note">
                  {{ t('Dashboard.ClaimTaxBack.Result.SwiftaidNote') }}
                </p>
              </div>
              <button
                type="button"
                class="sa-btn-purple sa-claim-route__action"
                @click="onUpgrade"
              >
                {{ t('Dashboard.ClaimTaxBack.Result.SwiftaidCta') }}
                <PaidPlanBadge />
              </button>
              <p class="sa-claim-route__fee">
                {{ t('Dashboard.ClaimTaxBack.Result.SwiftaidFee') }}
              </p>
            </article>
          </div>
        </section>

        <!-- Same common questions as the paid guide, including why the
             claimable figure is an "up to" estimate that HMRC may change. -->
        <ClaimFaq />
      </template>

      <section
        v-else
        class="sa-card sa-card--padded sa-claim-card"
        aria-labelledby="claim-result-heading"
      >
        <h2
          id="claim-result-heading"
          ref="resultHeading"
          class="sa-claim-result__headline"
          tabindex="-1"
        >
          {{ t('Dashboard.ClaimTaxBack.NothingOwed.Title') }}
        </h2>
        <!-- Owed nothing: say so plainly, no claim upsell (requirement). -->
        <p class="sa-claim-result__explain">
          {{
            t('Dashboard.ClaimTaxBack.NothingOwed.Body', { year: yearLabel })
          }}
        </p>
        <div class="sa-claim-record-offer">
          <h3 class="sa-claim-record-offer__title">
            {{ t('Dashboard.ClaimTaxBack.NothingOwed.RecordTitle') }}
          </h3>
          <p class="sa-claim-record-offer__body">
            {{ t('Dashboard.ClaimTaxBack.NothingOwed.RecordBody') }}
          </p>
          <button
            type="button"
            class="sa-claim-link-btn"
            @click="onUpgrade"
          >
            {{ t('Dashboard.ClaimTaxBack.NothingOwed.RecordCta') }}
          </button>
        </div>
        <button type="button" class="sa-claim-link-btn" @click="changeAnswers">
          {{ t('Dashboard.ClaimTaxBack.Result.ChangeAnswers') }}
        </button>
      </section>
    </div>
  </div>
</template>
